/**
 * ComposeAIAssist Component
 * Gmail-style AI writing assistant for message composition
 * 
 * The popover stays open during generation and shows the suggestion inline.
 */

import { useState } from 'react';
import { useFeedback } from '@/hooks/useFeedback';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';
import { cn } from '@/lib/utils';

import { TYPOGRAPHY } from '@/design-system';
import { LComponents } from '@/components/i18n';
import { useLocalizations } from '@/hooks/useLocalizations';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { 
  Sparkles, 
  Wand2, 
  ArrowDownWideNarrow, 
  Briefcase, 
  Loader2,
  Check,
  X,
} from 'lucide-react';
import type { ComposeFormData, Expert } from './types';

type AIAction = 'improve' | 'write' | 'shorter' | 'formal';

interface ComposeAIAssistProps {
  message: string;
  formData: ComposeFormData;
  selectedExperts: Expert[];
  onMessageChange: (message: string) => void;
  disabled?: boolean;
  inline?: boolean;
}

export function ComposeAIAssist({
  message,
  formData,
  selectedExperts,
  onMessageChange,
  disabled = false,
  inline = false,
}: ComposeAIAssistProps) {
  const { notify } = useFeedback();

  const { lComponents } = useLocalizations();
  const [isOpen, setIsOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedText, setGeneratedText] = useState<string | null>(null);
  const [currentAction, setCurrentAction] = useState<AIAction | null>(null);

  // Enable/disable logic
  const hasMessage = message.trim().length > 0;
  const hasSubject = formData.subject.trim().length > 0;
  const hasSubjectOrMessage = hasSubject || hasMessage;

  const handleAction = async (action: AIAction) => {
    setIsGenerating(true);
    setCurrentAction(action);
    setIsOpen(false);

    try {
      const recipientName = selectedExperts.length > 0 
        ? selectedExperts[0].display_name || selectedExperts[0].name 
        : undefined;

      const { data, error } = await supabase.functions.invoke('compose-message-assist', {
        body: {
          action,
          message: message || '',
          serviceType: formData.service_type,
          location: formData.location,
          recipientName,
          subject: formData.subject,
        },
      });

      if (error) throw error;

      if (data?.generatedText) {
        setGeneratedText(data.generatedText);
      } else {
        notify.error(
          'no_text_was_generated_please_try_again',
          'No text was generated. Please try again.'
        );
      }
    } catch (err) {
      logger.error('AI assist error:', err);
      notify.error(
        'failed_to_generate_text_please_try_again',
        'Failed to generate text. Please try again.'
      );
      setGeneratedText(null);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAccept = () => {
    if (generatedText) {
      onMessageChange(generatedText);
      setGeneratedText(null);
      setCurrentAction(null);
      setIsOpen(false);
    }
  };

  const handleReject = () => {
    setGeneratedText(null);
    setCurrentAction(null);
  };

  const actions: { id: AIAction; icon: React.ReactNode; labelId: string; defaultValue: string; requiresMessage: boolean }[] = [
    { 
      id: 'improve', 
      icon: <Wand2 className="h-4 w-4" />, 
      labelId: 'compose_message.ai_assist.improve', 
      defaultValue: 'Improve this',
      requiresMessage: true,
    },
    { 
      id: 'write', 
      icon: <Sparkles className="h-4 w-4" />, 
      labelId: 'compose_message.ai_assist.write', 
      defaultValue: 'Write for me',
      requiresMessage: false, // Can work with subject only
    },
    { 
      id: 'shorter', 
      icon: <ArrowDownWideNarrow className="h-4 w-4" />, 
      labelId: 'compose_message.ai_assist.shorter', 
      defaultValue: 'Make shorter',
      requiresMessage: true,
    },
    { 
      id: 'formal', 
      icon: <Briefcase className="h-4 w-4" />, 
      labelId: 'compose_message.ai_assist.formal', 
      defaultValue: 'More formal',
      requiresMessage: true,
    },
  ];

  // Prevent closing while generating
  const handleOpenChange = (open: boolean) => {
    if (isGenerating) return;
    setIsOpen(open);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled || isGenerating || !hasSubjectOrMessage}
          className={cn(
            "h-7 gap-1.5 text-muted-foreground",
            "hover:text-foreground hover:bg-muted/80",
            "transition-opacity duration-200",
            !inline && "absolute bottom-2 right-2"
          )}
        >
          {isGenerating ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
          <span className="text-xs">
            {isGenerating ? (
              <LComponents id="compose_message.ai_assist.generating" defaultValue="Writing..." />
            ) : (
              <LComponents id="compose_message.ai_assist.button" defaultValue="Help me write" />
            )}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-64 p-2 z-[100]" 
        align="end"
        onInteractOutside={(e) => {
          if (isGenerating || generatedText) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          if (isGenerating) {
            e.preventDefault();
          }
        }}
      >
        {/* Show suggestion inside popover when available */}
        {generatedText ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <span className={cn(TYPOGRAPHY.caption, 'font-medium')}>
                <LComponents id="compose_message.ai_assist.suggestion" defaultValue="AI Suggestion" />
              </span>
            </div>
            <p className="text-sm text-muted-foreground max-h-[200px] overflow-y-auto">
              {generatedText}
            </p>
            <div className="flex items-center gap-2 justify-end pt-2 border-t">
              <Button variant="ghost" size="sm" onClick={handleReject} className="h-7 px-2">
                <X className="h-4 w-4 mr-1" />
                <LComponents id="compose_message.ai_assist.reject" defaultValue="Discard" />
              </Button>
              <Button variant="default" size="sm" onClick={handleAccept} className="h-7 px-2">
                <Check className="h-4 w-4 mr-1" />
                <LComponents id="compose_message.ai_assist.accept" defaultValue="Use this" />
              </Button>
            </div>
          </div>
        ) : isGenerating ? (
          <div className="flex items-center justify-center py-6 gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">
              <LComponents id="compose_message.ai_assist.generating" defaultValue="Writing..." />
            </span>
          </div>
        ) : (
          <div className="flex flex-col">
            {actions.map((action) => {
              const isDisabled = action.requiresMessage ? !hasMessage : !hasSubjectOrMessage;
              return (
                <Button
                  key={action.id}
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start gap-2 h-9"
                  onClick={() => handleAction(action.id)}
                  disabled={isDisabled}
                >
                  {action.icon}
                  <LComponents id={action.labelId} defaultValue={action.defaultValue} />
                </Button>
              );
            })}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
